import { useTranslation } from 'react-i18next'
import { KILO } from '../../../constants/KILO_THEME'
import { ONBOARDING } from '../../../lib/onboarding/formStyles'
import type { BodyMapNodeDef, BodyMapNodeId, BodyProfile } from '../../../lib/calibration/onboardingConstants'
import { BODY_MAP_NODES } from '../../../lib/calibration/onboardingConstants'
import { bodyMapNodeLabel } from '../../../lib/i18nCatalog'
import type { OrthopedicInjuryInventory } from '../../../lib/calibrationTypes'

type BodyFrameMapProps = {
  profile: BodyProfile
  inventory: OrthopedicInjuryInventory
  activeNode: BodyMapNodeId | null
  onSelectNode: (nodeId: BodyMapNodeId) => void
}

function severityColor(inventory: OrthopedicInjuryInventory, nodeId: BodyMapNodeId): string {
  const entry = inventory[nodeId]
  if (!entry) return 'var(--kilo-accent)'
  if (entry.severity === 'mild') return '#34d399'
  if (entry.severity === 'moderate') return '#fbbf24'
  return '#f87171'
}

function HumanSilhouette({ profile }: { profile: BodyProfile }) {
  const head = profile === 'front'
  return (
    <g stroke="var(--kilo-soft)" strokeWidth="1.2" fill="none" opacity={0.85}>
      <ellipse cx="50" cy="12" rx="7" ry="8.5" />
      <path d="M38 22 Q50 28 62 22 L58 48 Q50 54 42 48 Z" />
      <path d="M42 48 L38 88 M58 48 L62 88" />
      <path d="M38 88 L34 92 M62 88 L66 92" />
      {head ? (
        <>
          <path d="M34 24 L26 44" />
          <path d="M66 24 L74 44" />
        </>
      ) : (
        <>
          <path d="M46 40 L50 48 L54 40" stroke="var(--kilo-muted)" strokeWidth="0.8" />
          <path d="M34 24 L28 42" />
          <path d="M66 24 L72 42" />
        </>
      )}
    </g>
  )
}

function NodeButton({
  node,
  inventory,
  isActive,
  onSelect,
  ariaLabel,
}: {
  node: BodyMapNodeDef
  inventory: OrthopedicInjuryInventory
  isActive: boolean
  onSelect: () => void
  ariaLabel: string
}) {
  const flagged = Boolean(inventory[node.id])
  const fill = severityColor(inventory, node.id)

  return (
    <g className="cursor-pointer" onClick={onSelect} role="button" aria-label={ariaLabel}>
      {flagged ? (
        <circle cx={node.cx} cy={node.cy} r={node.r + 2.5} fill={fill} opacity={0.18} />
      ) : null}
      <circle
        cx={node.cx}
        cy={node.cy}
        r={node.r}
        fill={flagged ? fill : 'var(--kilo-card)'}
        stroke={isActive ? 'var(--kilo-text)' : fill}
        strokeWidth={isActive ? 2.2 : 1.6}
        className="transition-all duration-200"
      />
      <circle cx={node.cx} cy={node.cy} r={1.6} fill={flagged ? 'var(--kilo-card)' : fill} opacity={0.9} />
    </g>
  )
}

const BodyFrameMap = ({ profile, inventory, activeNode, onSelectNode }: BodyFrameMapProps) => {
  const { t } = useTranslation()
  const nodes = BODY_MAP_NODES.filter((n) => n.profile === profile)

  return (
    <div className={`${KILO.bg.cardAlt} ${KILO.radius.nested} border border-kilo-line p-3`}>
      <svg viewBox="0 0 100 100" className="mx-auto h-[min(52vw,280px)] w-full max-w-xs" aria-hidden>
        <rect x="8" y="4" width="84" height="92" rx="12" fill="var(--kilo-alt)" stroke="var(--kilo-line)" />
        <HumanSilhouette profile={profile} />
        {nodes.map((node) => (
          <NodeButton
            key={`${profile}-${node.id}`}
            node={node}
            inventory={inventory}
            isActive={activeNode === node.id}
            onSelect={() => onSelectNode(node.id)}
            ariaLabel={bodyMapNodeLabel(t, node.id)}
          />
        ))}
      </svg>
      <p className={`mt-2 text-center text-[11px] ${ONBOARDING.mutedSoft}`}>
        Tap a joint node · {profile === 'front' ? 'Anterior' : 'Posterior'} view
      </p>
    </div>
  )
}

export default BodyFrameMap
